import React, { useState, useEffect } from "react";
import { Button, Checkbox, FormControlLabel } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import useFormStore from "../state/useFormStore";
import { apiService } from "../utils/api";
import { config } from "../config";
import styled from "styled-components";
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import CloseIcon from '@mui/icons-material/Close';
import FingerprintJS from '@fingerprintjs/fingerprintjs';

const Container = styled.div`
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  padding: 20px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Description = styled.p`
  font-size: 16px;
  color: #333;
  text-align: center;
  margin-bottom: 24px;
  max-width: 600px;
`;

const PdfContainer = styled.div`
  width: 100%;
  height: 400px;
  position: relative;
  background: #f5f5f5;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 24px;
`;

const PdfViewer = styled.object`
  width: 100%;
  height: 100%;
  border: none;
`;

const ViewButton = styled.button`
  width: 100%;
  max-width: 600px;
  background: #f5f5f5;
  color: #333;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 14px;
  margin-top: 10px;
  transition: all 0.2s ease;

  &:hover {
    background: #e8e8e8;
  }
`;

const ModalOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  display: ${({ isOpen }) => isOpen ? 'flex' : 'none'};
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  width: 95%;
  height: 95%;
  background: white;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  position: relative;
`;

const ModalHeader = styled.div`
  padding: 16px;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  color: #333;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #666;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalBody = styled.div`
  flex: 1;
  overflow: hidden;
  position: relative;
`;

const FullscreenPdfViewer = styled.object`
  width: 100%;
  height: 100%;
  border: none;
`;

const CheckboxContainer = styled.div`
  width: 100%;
  max-width: 600px;
  margin: 16px 0;
`;

const ActionButton = styled(Button)`
  width: 100%;
  max-width: 600px;
  height: 48px;
  border-radius: 50px;
  font-size: 16px;
  font-weight: 600;
  text-transform: none;
  margin-top: 24px;
`;

const TermsLink = styled.span`
  color: #0066cc;
  text-decoration: underline;
  cursor: pointer;
  &:hover {
    color: #004499;
  }
`;

const Firma: React.FC = () => {
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedContract, setAcceptedContract] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [checksum, setChecksum] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [deviceFingerprint, setDeviceFingerprint] = useState<string | null>(null);
  const navigate = useNavigate();
  const { hash } = useParams();

  const { requestUid, setRequestUid } = useFormStore((state) => ({
    requestUid: state.requestUid,
    setRequestUid: state.setRequestUid,
  }));

  // Inicializar FingerprintJS
  useEffect(() => {
    const initFingerprint = async () => {
      try {
        const fp = await FingerprintJS.load();
        const result = await fp.get();
        setDeviceFingerprint(result.visitorId);
        console.log('Device Fingerprint:', result.visitorId);
      } catch (error) {
        console.error('Error al generar fingerprint:', error);
      }
    };

    initFingerprint();
  }, []);

  // Obtener requestUid de localStorage
  useEffect(() => {
    const fetchRequestUid = async () => {
      if (!hash) return;

      try {
        const storedData = localStorage.getItem(`prestix_${hash}`);
        if (storedData) {
          const data = JSON.parse(storedData);
          if (data.requestUid) {
            setRequestUid(data.requestUid);
            console.log("requestUid obtenido:", data.requestUid);
          }
        }
      } catch (error) {
        console.error("Error al obtener requestUid:", error);
      }
    };

    fetchRequestUid();
  }, [hash, setRequestUid]);

  // Obtener términos y condiciones
  useEffect(() => {
    const fetchContract = async () => {
      try {
        if (!requestUid) {
          throw new Error("No hay requestUid disponible");
        }
  
        const response = await apiService.requests.getContract(requestUid);

        if (!response.data?.data) throw new Error("Error obteniendo términos");

        const { documentUrl, version } = response.data.data;
        setPdfUrl(documentUrl);
        setChecksum(version);
        console.log("Términos obtenidos:", response.data.data);
      } catch (error) {
        console.error("Error obteniendo términos y condiciones:", error);
      }
    };

    fetchContract();
  }, [requestUid]);

  const handleAcceptTerms = () => {
    setAcceptedTerms(!acceptedTerms);
  };

  const handleAcceptContract = () => {
    setAcceptedContract(!acceptedContract);
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const toggleTermsModal = () => {
    setShowTermsModal(!showTermsModal);
  };

  // Enviar aceptación de términos
  const submitTermsAcceptance = async () => {
    if (!requestUid || !checksum || !deviceFingerprint) return;

    try {
      const response = await apiService.requests.signContract(requestUid, {
        screenResolution: `${window.screen.width}x${window.screen.height}`,
        fingerprint: deviceFingerprint,
        termsVersion: "1.0.0",
        deviceInfo: {
          userAgent: navigator.userAgent,
          language: navigator.language,
          platform: navigator.platform,
          screenResolution: `${window.screen.width}x${window.screen.height}`,
          colorDepth: window.screen.colorDepth,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        }
      });

      if (response.data) {
        console.log("Términos aceptados correctamente:", response.data);
        
        // Actualizar datos en localStorage
        const storedData = localStorage.getItem(`prestix_${hash}`);
        if (storedData) {
          const data = JSON.parse(storedData);
          data.termsAccepted = true;
          data.termsAcceptedAt = new Date().toISOString();
          data.deviceFingerprint = deviceFingerprint;
          localStorage.setItem(`prestix_${hash}`, JSON.stringify(data));
        }

        navigate(`/${hash}/confirmacion`);
      } else {
        throw new Error("Error al aceptar términos");
      }
    } catch (error) {
      console.error("Error en la aceptación de términos:", error);
    }
  };

  return (
    <Container>
      <Description>
        Para finalizar el proceso de solicitud, es necesario que leas y aceptes los términos y condiciones del préstamo.
      </Description>

      <PdfContainer>
        {pdfUrl ? (
          <>
            <PdfViewer
              data={pdfUrl}
              type="application/pdf"
              title="Contrato de adhesión"
            >
              <p>Tu navegador no puede mostrar el PDF directamente. 
                {pdfUrl && (
                  <a href={pdfUrl} target="_blank" rel="noopener noreferrer">
                    Haz clic aquí para descargarlo
                  </a>
                )}
              </p>
            </PdfViewer>
          </>
        ) : (
          <p>Cargando términos y condiciones...</p>
        )}
      </PdfContainer>

      {pdfUrl && (
        <ViewButton onClick={toggleFullscreen}>
          <FullscreenIcon fontSize="small" />
          <span>Ver contrato en pantalla completa</span>
        </ViewButton>
      )}

      <ModalOverlay isOpen={isFullscreen}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>Contrato de adhesión</ModalTitle>
            <CloseButton onClick={toggleFullscreen}>
              <CloseIcon />
            </CloseButton>
          </ModalHeader>
          <ModalBody>
            <FullscreenPdfViewer
              data={pdfUrl || ''}
              type="application/pdf"
              title="Contrato de adhesión"
            >
              <p>Tu navegador no puede mostrar el PDF directamente. 
                {pdfUrl && (
                  <a href={pdfUrl} target="_blank" rel="noopener noreferrer">
                    Haz clic aquí para descargarlo
                  </a>
                )}
              </p>
            </FullscreenPdfViewer>
          </ModalBody>
        </ModalContent>
      </ModalOverlay>

      <ModalOverlay isOpen={showTermsModal}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>Términos y Condiciones Generales</ModalTitle>
            <CloseButton onClick={toggleTermsModal}>
              <CloseIcon />
            </CloseButton>
          </ModalHeader>
          <ModalBody>
            <FullscreenPdfViewer
              data={config.business.termsUrl}
              type="application/pdf"
              title="Términos y Condiciones Generales"
            >
              <p>Tu navegador no puede mostrar el PDF directamente. 
                <a href={config.business.termsUrl} target="_blank" rel="noopener noreferrer">
                  Haz clic aquí para descargarlo
                </a>
              </p>
            </FullscreenPdfViewer>
          </ModalBody>
        </ModalContent>
      </ModalOverlay>

      <CheckboxContainer>
        <FormControlLabel
          control={
            <Checkbox 
              checked={acceptedContract} 
              onChange={handleAcceptContract} 
              color="primary" 
            />
          }
          label="Leí y acepto las condiciones del contrato de adhesión."
        />

        <FormControlLabel
          control={
            <Checkbox 
              checked={acceptedTerms} 
              onChange={handleAcceptTerms} 
              color="primary" 
            />
          }
          label={
            <span>
              Al continuar, acepto también los{" "}
              <TermsLink onClick={toggleTermsModal}>
                Términos y Condiciones Generales
              </TermsLink>.
            </span>
          }
        />
      </CheckboxContainer>

      <ActionButton
        variant="contained"
        onClick={submitTermsAcceptance}
        disabled={!(acceptedTerms && acceptedContract)}
        style={{
          backgroundColor: "#FFF200",
          color: "#000000",
          opacity: (acceptedTerms && acceptedContract) ? 1 : 0.2,
        }}
      >
        Finalizar
      </ActionButton>
    </Container>
  );
};

export default Firma;